import { useState, useEffect, useCallback } from 'react'

export interface DashboardMetrics {
  exchangeProgress: { percent: number; label: string }
  escrowBalance: { cents: number | null; locked: boolean; lockReason?: string }
  escrowInterest: { cents: number | null; apyPercent: number; bankName: string; daysHeld: number }
  documents: { count: number }
  taxSavings: { cents: number | null; lastCalculatedAt: string | null }
  taxLiabilityWithout1031: { cents: number | null }
  reinvestmentAvailable: { cents: number | null; phase: 'pre-data' | 'estimated' | 'actual' }
}

const DEFAULT_APY_PERCENT = 3
const DEFAULT_BANK_NAME = 'Western Alliance Bank'

const CALCULATOR_KEY_PREFIX = 'open1031:calculator:'
const DOCS_KEY_PREFIX = 'open1031:docs:'

export interface CalculatorStoredData {
  taxSavingsCents: number
  calculatedAt: string
  inputs: {
    sellingPrice?: number
    sellingExpenses?: number
    [key: string]: unknown
  }
  results?: {
    taxWithout1031?: {
      totalTax?: number
    }
    taxOn1031?: {
      totalTax?: number
    }
  }
}

function loadCalculatorData(userId: string): CalculatorStoredData | null {
  const raw = localStorage.getItem(`${CALCULATOR_KEY_PREFIX}${userId}`)
  if (!raw) return null
  try {
    return JSON.parse(raw) as CalculatorStoredData
  } catch {
    return null
  }
}

function countHubDocState(): number {
  const canonicalFlags: Array<string> = [
    'identification_hud1_extracted',
    'identification_form_signed',
    'exchange_agreement_signed',
    'w9_signed',
    'purchase_hub_assignment_signed',
    'purchase_hub_notice_seller_signed',
    'disbursement_authorization_signed',
  ]
  const counted = new Set<string>()
  for (const flag of canonicalFlags) {
    if (localStorage.getItem(flag) === 'true') counted.add(flag)
  }
  return counted.size
}

function writeDocCount(userId: string, count: number): void {
  try {
    localStorage.setItem(`${DOCS_KEY_PREFIX}${userId}`, JSON.stringify({ count }))
  } catch {
  }
}

function loadDocCount(userId: string): number {
  const hubCount = countHubDocState()
  if (hubCount > 0) {
    writeDocCount(userId, hubCount)
    return hubCount
  }
  const raw = localStorage.getItem(`${DOCS_KEY_PREFIX}${userId}`)
  if (!raw) return 0
  try {
    const parsed = JSON.parse(raw) as { count: number }
    return typeof parsed.count === 'number' ? parsed.count : 0
  } catch {
    return 0
  }
}

function loadEscrowBalance(userId: string): number | null {
  const raw = localStorage.getItem(`escrow_data_${userId}`)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { principalAmount?: number }
    if (typeof parsed.principalAmount === 'number') {
      return Math.round(parsed.principalAmount * 100)
    }
    return null
  } catch {
    return null
  }
}

function loadEscrowPrincipalCents(userId: string): number | null {
  const raw = localStorage.getItem(`escrow_data_${userId}`)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { principalAmount?: number }
    if (typeof parsed.principalAmount === 'number') {
      return Math.round(parsed.principalAmount * 100)
    }
    return null
  } catch {
    return null
  }
}

function computeEscrowInterest(
  userId: string,
  locked: boolean,
): { cents: number | null; apyPercent: number; bankName: string; daysHeld: number } {
  if (!userId || locked) {
    return { cents: null, apyPercent: DEFAULT_APY_PERCENT, bankName: DEFAULT_BANK_NAME, daysHeld: 0 }
  }
  try {
    const raw = localStorage.getItem(`escrow_data_${userId}`)
    if (!raw) {
      return { cents: null, apyPercent: DEFAULT_APY_PERCENT, bankName: DEFAULT_BANK_NAME, daysHeld: 0 }
    }
    const parsed = JSON.parse(raw) as {
      principalAmount?: number
      fundedAt?: string
      depositDate?: string
      annualRatePercent?: number
      bankName?: string
    }
    const principal = typeof parsed.principalAmount === 'number' ? parsed.principalAmount : 0
    const apy = typeof parsed.annualRatePercent === 'number' ? parsed.annualRatePercent : DEFAULT_APY_PERCENT
    const bank = typeof parsed.bankName === 'string' && parsed.bankName.trim() ? parsed.bankName : DEFAULT_BANK_NAME
    const depositIso = parsed.depositDate ?? parsed.fundedAt
    const depositMs = depositIso ? new Date(depositIso).getTime() : Date.now()
    const daysHeld = Math.max(0, Math.floor((Date.now() - depositMs) / (1000 * 60 * 60 * 24)))
    const interest = principal * (apy / 100) * (daysHeld / 365)
    return {
      cents: Math.round(interest * 100),
      apyPercent: apy,
      bankName: bank,
      daysHeld,
    }
  } catch {
    return { cents: null, apyPercent: DEFAULT_APY_PERCENT, bankName: DEFAULT_BANK_NAME, daysHeld: 0 }
  }
}

function computeReinvestmentAvailable(
  calcData: CalculatorStoredData | null,
  escrowPrincipalCents: number | null,
  salesHubComplete: boolean,
): { cents: number | null; phase: 'pre-data' | 'estimated' | 'actual' } {
  if (!salesHubComplete) return { cents: null, phase: 'pre-data' }
  if (escrowPrincipalCents !== null) {
    return { cents: escrowPrincipalCents, phase: 'actual' }
  }
  if (calcData) {
    const { sellingPrice, sellingExpenses } = calcData.inputs
    if (typeof sellingPrice === 'number' && typeof sellingExpenses === 'number') {
      const amountRealized = sellingPrice - sellingExpenses
      return { cents: Math.round(amountRealized * 100), phase: 'estimated' }
    }
  }
  return { cents: null, phase: 'pre-data' }
}

export function useDashboardMetrics(
  userId: string | undefined,
  hubBasedProgress: number,
  salesHubComplete: boolean,
  uploadedDocsCount: number
): DashboardMetrics {
  const [metrics, setMetrics] = useState<DashboardMetrics>(() =>
    computeMetrics(userId, hubBasedProgress, salesHubComplete, uploadedDocsCount)
  )

  const refresh = useCallback(() => {
    setMetrics(computeMetrics(userId, hubBasedProgress, salesHubComplete, uploadedDocsCount))
  }, [userId, hubBasedProgress, salesHubComplete, uploadedDocsCount])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    const handleStorage = () => refresh()
    window.addEventListener('calculatorResultPersisted', handleStorage)
    window.addEventListener('wizardDataUpdated', handleStorage)
    window.addEventListener('activityAdded', handleStorage)
    window.addEventListener('escrowDataUpdated', handleStorage)
    window.addEventListener('documentsUpdated', handleStorage)
    return () => {
      window.removeEventListener('calculatorResultPersisted', handleStorage)
      window.removeEventListener('wizardDataUpdated', handleStorage)
      window.removeEventListener('activityAdded', handleStorage)
      window.removeEventListener('escrowDataUpdated', handleStorage)
      window.removeEventListener('documentsUpdated', handleStorage)
    }
  }, [refresh])

  return metrics
}

function computeMetrics(
  userId: string | undefined,
  hubBasedProgress: number,
  salesHubComplete: boolean,
  uploadedDocsCount: number
): DashboardMetrics {
  const uid = userId ?? ''

  const calcData = uid ? loadCalculatorData(uid) : null
  const docCount = uid ? loadDocCount(uid) : uploadedDocsCount
  const escrowCents = uid ? loadEscrowBalance(uid) : null
  const escrowPrincipalCents = uid ? loadEscrowPrincipalCents(uid) : null

  const escrowLocked = !salesHubComplete
  const escrowLockReason = escrowLocked
    ? 'Complete identity verification to enable escrow'
    : undefined

  const progressLabel =
    hubBasedProgress === 0
      ? 'Start an exchange'
      : hubBasedProgress === 100
      ? 'All hubs complete'
      : 'Overall completion'

  const taxLiabilityRaw = calcData?.results?.taxWithout1031?.totalTax
  const taxLiabilityCents =
    salesHubComplete && taxLiabilityRaw != null ? Math.round(taxLiabilityRaw * 100) : null

  const reinvestmentAvailable = computeReinvestmentAvailable(calcData, escrowPrincipalCents, salesHubComplete)

  const escrowInterest = computeEscrowInterest(uid, escrowLocked)

  return {
    exchangeProgress: {
      percent: hubBasedProgress,
      label: progressLabel,
    },
    escrowBalance: {
      cents: escrowLocked ? null : escrowCents,
      locked: escrowLocked,
      lockReason: escrowLockReason,
    },
    escrowInterest,
    documents: {
      count: docCount > 0 ? docCount : uploadedDocsCount,
    },
    taxSavings: {
      cents: calcData ? calcData.taxSavingsCents : null,
      lastCalculatedAt: calcData ? calcData.calculatedAt : null,
    },
    taxLiabilityWithout1031: {
      cents: taxLiabilityCents,
    },
    reinvestmentAvailable,
  }
}
